/**
 * 학교(워크스페이스) 데이터 파트(domain)별 revision.
 * `workspaces.data_revisions`의 키와 같다. 저장 시 해당 파트만 올리고, 다른 기기는 바뀐 파트의 캐시만 비운다.
 *
 * 캐시 키·쿼리 키 → 파트 분류는 "그 파트의 원본 데이터만 담는 캐시"만 등록한다.
 * 등록되지 않은(여러 데이터를 조합한 파생 캐시 등) 키는 어느 파트든 바뀌면 비운다 — 정합성 우선.
 */

export const SCHOOL_DATA_DOMAINS = [
  /** 클래스(학년)·편제(tracks)·학년 대표 */
  'gradeTrack',
  /** 편제 페이지 섹션 레이아웃(workspaces.grade_track_sections) */
  'gradeTrackSections',
  /** 편제 프로그램 모드·대표 시간표·시수조정(workspaces.grade_track_program_preferences) */
  'gradeTrackPrefs',
  /** 학년도·학기·학사일정 일자·학사일정 섹션 */
  'academicCalendar',
  /** 수업 스케줄(교시) 설정 */
  'classSchedule',
  /** 프로그램 색상·기초시간표 틀(workspaces.program_color_settings) */
  'programColors',
  /** 기초시간표 셀(base_timetable_cells) */
  'baseTimetable',
  /** 연간시간표 셀·주간 메모 */
  'annualTimetable',
  /** 연간 프로젝트 목록 */
  'annualProjects',
  /** 프로그램 내용 행·진도 셀 */
  'programContent',
  /** 워크스페이스 학습내용 템플릿 */
  'learningContent',
  /** 학급 멤버·레이아웃 */
  'classMembers',
  /** 포인트 항목·과제 포인트 노트 */
  'pointNotes',
  /** 연간 진도 행동특성 기록 */
  'behaviorNotes',
  /** 주간학습안내 메모·편제 색상 */
  'weeklyGuide',
  /** 학교 이름 등 워크스페이스 기본 정보 */
  'workspaceProfile',
] as const;

export type SchoolDataDomain = (typeof SCHOOL_DATA_DOMAINS)[number];

export type SchoolDataRevisions = Partial<Record<SchoolDataDomain, string>>;

export function isSchoolDataDomain(value: unknown): value is SchoolDataDomain {
  return typeof value === 'string' && (SCHOOL_DATA_DOMAINS as readonly string[]).includes(value);
}

export function normalizeSchoolDataRevisions(raw: unknown): SchoolDataRevisions {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: SchoolDataRevisions = {};
  Object.entries(raw as Record<string, unknown>).forEach(([key, value]) => {
    if (!isSchoolDataDomain(key)) return;
    if (typeof value === 'string' && value.trim()) out[key] = value.trim();
  });
  return out;
}

/** 로컬 기록과 서버 revision이 다른 파트 */
export function computeStaleSchoolDataDomains(
  local: SchoolDataRevisions,
  remote: SchoolDataRevisions
): SchoolDataDomain[] {
  return SCHOOL_DATA_DOMAINS.filter((domain) => (local[domain] ?? '') !== (remote[domain] ?? ''));
}

/** 원본 캐시 LS 키 접두사 → 파트. (워크스페이스 id 포함 여부는 호출부에서 따로 확인) */
const CACHE_KEY_PREFIX_DOMAINS: ReadonlyArray<readonly [string, SchoolDataDomain]> = [
  ['cp.grades.', 'gradeTrack'],
  ['cp.tracks.', 'gradeTrack'],
  ['cp.gradeRepresentatives.', 'gradeTrack'],
  ['cp.gradeTrackSections.', 'gradeTrackSections'],
  ['cp.gradeTrackProgramPrefs.', 'gradeTrackPrefs'],
  ['cp.academicYearSettings.', 'academicCalendar'],
  ['cp.academicTerms.', 'academicCalendar'],
  ['cp.calendarDays.', 'academicCalendar'],
  ['cp.academicCalendarSections.', 'academicCalendar'],
  ['cp.academicSectionSettings.', 'academicCalendar'],
  ['cp.academicSectionCalendarDays.', 'academicCalendar'],
  ['cp.classScheduleSettings.', 'classSchedule'],
  ['cp.programColorSettings.', 'programColors'],
  ['cp.annualTimetableCells.', 'annualTimetable'],
  ['cp.annualTimetableWeekMemos.', 'annualTimetable'],
  ['cp.programFixedContentRows.', 'programContent'],
  ['cp.programScopedContentRows.', 'programContent'],
  ['cp.programProgressCells.', 'programContent'],
  ['cp.annualProgressScopedTerm.', 'programContent'],
  ['cp.workspaceLearningContent.', 'learningContent'],
  ['cp-class-members:', 'classMembers'],
  ['cp.pointItemNote.', 'pointNotes'],
  ['cp.assignmentPointsNote.', 'pointNotes'],
  ['cp.assignmentPoints.', 'pointNotes'],
  ['cp.annualBehaviorNotes.', 'behaviorNotes'],
  ['cp.weeklyLearningGuideNotes.', 'weeklyGuide'],
  ['cp.weeklyLearningGuideTrackColors.', 'weeklyGuide'],
  ['cp.annualProjectPrograms.', 'annualProjects'],
];

/** 등록된 원본 캐시면 파트, 아니면 null(파생·미분류 → 어느 파트든 바뀌면 비움) */
export function schoolCacheKeyDomain(key: string): SchoolDataDomain | null {
  for (const [prefix, domain] of CACHE_KEY_PREFIX_DOMAINS) {
    if (key.startsWith(prefix)) return domain;
  }
  return null;
}

/** 학교 React Query 키(['school', ws, <segment>, ...])의 segment → 파트 */
const QUERY_SEGMENT_DOMAINS: Readonly<Record<string, SchoolDataDomain>> = {
  annualTimetableCells: 'annualTimetable',
  annualTimetableWeekMemos: 'annualTimetable',
  programProgressCells: 'programContent',
  programScopedRows: 'programContent',
  programScopedContentRows: 'programContent',
  programFixedRows: 'programContent',
  calendarDays: 'academicCalendar',
  gradeTrackSections: 'gradeTrackSections',
  annualProjectPrograms: 'annualProjects',
  annualBehaviorNotes: 'behaviorNotes',
  weeklyLearningGuideNotes: 'weeklyGuide',
  weeklyLearningGuideTrackColors: 'weeklyGuide',
};

export function schoolQueryKeyDomain(queryKey: readonly unknown[]): SchoolDataDomain | null {
  const segment = queryKey[2];
  if (typeof segment !== 'string') return null;
  return QUERY_SEGMENT_DOMAINS[segment] ?? null;
}

/** 파트 변경 시 비울 대상인지: 해당 파트의 원본 캐시이거나, 미분류(파생) 캐시 */
export function isAffectedBySchoolDataDomains(
  keyDomain: SchoolDataDomain | null,
  changed: ReadonlySet<SchoolDataDomain>
): boolean {
  if (changed.size === 0) return false;
  return keyDomain == null || changed.has(keyDomain);
}
